/**
 * Creates a changePerPage handler function that updates perPage and adjusts offset accordingly.
 *
 * The function ensures that:
 * - The perPage value is updated
 * - The offset is adjusted to maintain the current page when possible
 * - If the new perPage is greater than the current offset, or the offset is not divisible by perPage,
 *   the offset is adjusted to the nearest valid page boundary
 *
 * @param offset - Current offset value
 * @param setPerPage - State setter for perPage
 * @param setOffset - State setter for offset
 * @returns A function that accepts a new perPage value and updates both perPage and offset accordingly
 *
 * @example
 * ```typescript
 * const changePerPage = createChangePerPageHandler(
 *   offset,
 *   setPerPage,
 *   setOffset
 * );
 * ```
 */
export function createChangePerPageHandler(
  offset: number,
  setPerPage: (value: number) => void,
  setOffset: (value: number) => void
): (newPerPage: number) => void {
  return (newPerPage: number) => {
    setPerPage(newPerPage);
    if (newPerPage > offset || offset % newPerPage !== 0) {
      setOffset(Math.floor(offset / newPerPage) * newPerPage);
    }
  };
}
